/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { useMemo, useState } from '@web-stories-wp/react';
import {
  THEME_CONSTANTS,
  Button,
  Icons,
  Text,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from '@web-stories-wp/design-system';
import { __ } from '@web-stories-wp/i18n';
import {
  createSolid,
  hasOpacity,
  hasGradient,
  type Solid,
  type Pattern,
} from '@web-stories-wp/patterns';

/**
 * Internal dependencies
 */
import { INPUT_GAP } from './constants';
import Header from './header';
import BasicColorList from './basicColorList';
import { SearchInput } from '../library/common';
import type { SavedColor } from '../../app/savedColors/types';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${INPUT_GAP}px;
`;

const Footer = styled.div`
  flex: 1;
  margin-top: 16px;
`;

const Label = styled(Text).attrs({
  size: THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL,
})``;

const DefaultText = styled(Label)`
  margin-left: 8px;
`;

const EmptyStateText = styled(Label)`
  text-align: center;
  color: ${({ theme }) => theme.colors.border.defaultPress};
`;

const StyledButton = styled(Button)`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledPlus = styled(Icons.PlusOutline)`
  width: 32px;
  margin: -8px 0;
`;

interface SavedColorPickerProps {
  allowsOpacity: boolean;
  allowsGradient: boolean;
  color: Solid | Pattern | null;
  handleColorChange: (color: SavedColor) => void;
  handleApplyColor: (color: SavedColor) => void;
  handleClose: () => void;
  handleDelete: (color: SavedColor) => void;
  savedColors: SavedColor[];
  showCustomPicker: (color: SavedColor, isSelectedColor: boolean) => void;
}

function SavedColorPicker({
  allowsOpacity,
  allowsGradient,
  color,
  handleColorChange,
  handleApplyColor,
  handleClose,
  handleDelete,
  savedColors,
  showCustomPicker,
}: SavedColorPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const colors = useMemo(
    () =>
      savedColors
        .filter((savedColor) => {
          return (
            (allowsGradient || !hasGradient(savedColor.color)) &&
            (allowsOpacity || !hasOpacity(savedColor.color))
          );
        })
        .filter((savedColor) => {
          return savedColor.name
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        }),
    [allowsOpacity, allowsGradient, savedColors, searchTerm]
  );

  return (
    <>
      <Header handleClose={handleClose}>
        <DefaultText id="colorpicker-default-colors-title">
          {__('Colors', 'web-stories')}
        </DefaultText>
      </Header>
      <Body>
        <SearchInput
          incremental
          initialValue=""
          onSearch={(newSearchTerm) => setSearchTerm(newSearchTerm)}
          placeholder={__('Search', 'web-stories')}
        />
        {colors.length > 0 ? (
          <BasicColorList
            color={color}
            colors={colors}
            allowsOpacity={allowsOpacity}
            allowsGradient={allowsGradient}
            aria-labelledby="colorpicker-default-colors-title"
            handleColorChange={handleApplyColor}
            handleError={
              color?.error ? (color) => color.error.onRetry(color) : undefined
            }
            handleEdit={(color, isSelectedColor) =>
              showCustomPicker(color, isSelectedColor)
            }
            handleDelete={(color) => handleDelete(color)}
          />
        ) : (
          <>
            {searchTerm ? (
              <EmptyStateText>
                {__('No matches.', 'web-stories')}
              </EmptyStateText>
            ) : (
              <EmptyStateText>
                {__('No saved colors.', 'web-stories')}
              </EmptyStateText>
            )}
          </>
        )}
      </Body>
      <Footer>
        <StyledButton
          onClick={() => {
            const newSavedColor = {
              color: {
                ...(color || createSolid(0, 0, 0)),
                id: uuidv4(),
              },
              name: '',
              error: null,
              isLocal: true,
              shouldSync: false,
            };
            showCustomPicker(newSavedColor, true);
            handleColorChange(newSavedColor);
          }}
          type={BUTTON_TYPES.SECONDARY}
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.RECTANGLE}
        >
          {__('Custom', 'web-stories')} <StyledPlus />
        </StyledButton>
      </Footer>
    </>
  );
}

export default SavedColorPicker;
